/**
 * Each plan's Auto Fix usage, as a monthly pool of fixes for the whole organization: issues and
 * CI runs that fixed something count alike. The ledger pauses PRs by these numbers, the usage page
 * shows them, and an upgrade offer names the next plan. The pricing page describes each plan in
 * words instead, so a pool can change without changing a promise. To change a plan's pool, edit it
 * here in a pull request; `__tests__/config.test.ts` checks the bounds and the upgrade steps.
 *
 * Client-safe: `config.ts` adds the overrides, which name customers, on the server.
 */
import { MAX_PLAN_USAGE_MULTIPLIER } from '@/types/billing-plan';

export type FixedRule = { kind: 'fixed'; fixes: number };
export type UnlimitedRule = { kind: 'unlimited' };
export type AutoFixAllowanceRule =
  | {
      kind: 'per_seat';
      fixesPerSeat: number;
      /** Caps seats times `fixesPerSeat`. Absent means no cap. */
      maxFixes?: number;
    }
  | FixedRule
  | UnlimitedRule;

export type AutoFixPlanAllowances = {
  team: AutoFixAllowanceRule;
  pro: AutoFixAllowanceRule;
  max: AutoFixAllowanceRule;
  trial: AutoFixAllowanceRule;
  // Free installs have no seat count, so their rule cannot scale with seats.
  free: FixedRule | UnlimitedRule;
};

const PRO_FIXES_PER_SEAT = 50;

export const AUTO_FIX_PLAN_ALLOWANCES: AutoFixPlanAllowances = {
  team: { kind: 'per_seat', fixesPerSeat: 10 },
  pro: { kind: 'per_seat', fixesPerSeat: PRO_FIXES_PER_SEAT },
  max: { kind: 'per_seat', fixesPerSeat: PRO_FIXES_PER_SEAT * MAX_PLAN_USAGE_MULTIPLIER },
  // No more per developer than Team, so a trial grows by adding members; the cap bounds the largest.
  trial: { kind: 'per_seat', fixesPerSeat: 10, maxFixes: 200 },
  free: { kind: 'fixed', fixes: 0 },
};

/**
 * The fixes a rule gives an organization with `seats` seats each period, or null for no limit.
 * Free installs have no seats, so they pass 0 and a per-seat rule gives them none.
 */
export function autoFixPoolFixes(rule: AutoFixAllowanceRule, seats: number): number | null {
  switch (rule.kind) {
    case 'per_seat': {
      const fixes = seats * rule.fixesPerSeat;
      return rule.maxFixes === undefined ? fixes : Math.min(fixes, rule.maxFixes);
    }
    case 'fixed':
      return rule.fixes;
    case 'unlimited':
      return null;
  }
}
